// The strip that says a newer brain is live — 8 Sep 2026. The two rules are in src/version.ts,
// pure and tested there; this owns the timer, the fetch and the strip.
//
// WHEN IT ASKS. Every few minutes while the tab is visible (api/intervals.ts), and the moment a
// hidden tab becomes visible again — the overnight tab is the case this exists for, and it should
// learn on the first glance, not minutes into the morning. Never at mount: a page that has just
// loaded index.html holds the current build by definition, so the CI render (T39) makes no
// request here at all.
//
// WHAT IT ASKS. Its own origin's index.html, `no-store`, so the CDN answers with what the last
// deploy published rather than what this browser cached. No token, no BFF: the check is against
// the bundle's home, and it costs the service nothing. A failed fetch — offline, a CDN hiccup —
// is nothing; the next check asks again.
//
// WHAT IT DOES NOT DO. Reload on its own. A recruiter may be mid-sentence in a compose bar or
// halfway through an entry form, and both are lost on a reload; the strip says so and waits.
import { useEffect, useState } from 'react';
import { POLL_MS } from '../api/intervals.ts';
import { Button } from '../ui/atoms.tsx';
import { entryScriptOf, isNewerBuild } from '../version.ts';

/** The entry script THIS page loaded, read from the document rather than from any constant. */
function runningEntryScript(): string | null {
  return document.querySelector('script[type="module"][src]')?.getAttribute('src') ?? null;
}

export function useNewerBuild(): boolean {
  const [newer, setNewer] = useState(false);

  useEffect(() => {
    const running = runningEntryScript();
    if (running === null) return;
    let stopped = false;

    const check = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const response = await fetch('/index.html', { cache: 'no-store', credentials: 'omit' });
        if (!response.ok) return;
        const fetched = entryScriptOf(await response.text());
        if (!stopped && isNewerBuild(running, fetched)) setNewer(true);
      } catch {
        // Offline, or the CDN did not answer. The next check asks again.
      }
    };

    const onVisibility = () => void check();
    const timer = setInterval(() => void check(), POLL_MS.newVersion);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return newer;
}

export function NewVersionStrip() {
  const newer = useNewerBuild();
  if (!newer) return null;
  return (
    <div className="new-version" role="status">
      <span>
        A newer version of the brain is live. Reload when you are between things. Anything typed and
        not yet saved goes with it.
      </span>
      <Button kind="accent" size="sm" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </div>
  );
}
